<!--
  RegisterForm.vue
  Vue 3 authentication component for user registration

  Features:
  - Username, email, and password validation
  - Password confirmation matching
  - Loading states during API calls
  - Error message display
  - Success event emission
  - Preserves vanilla CSS classes for styling

  Props: None

  Emits:
  - success: When registration is successful, passes user data
  - switchToLogin: When user clicks "Back to Login"

  Usage:
  <RegisterForm @success="handleRegisterSuccess" @switchToLogin="showLogin" />
-->

<script setup lang="ts">
import { ref, computed } from 'vue'

// API URL configuration (matches vanilla JS)
const isDevPort = window.location.port && !['80', '443', ''].includes(window.location.port)
const API_URL = (window.location.hostname === 'localhost' && isDevPort)
  ? `${window.location.protocol}//localhost:8000/api`
  : `${window.location.origin}/api`

// Component state
const username = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const loading = ref(false)
const errorMessage = ref('')

// Emits
const emit = defineEmits<{
  success: [user: any]
  switchToLogin: []
}>()

// Validation
const usernameError = computed(() => {
  if (!username.value) return ''
  return username.value.length >= 3 ? '' : 'Username must be at least 3 characters'
})

const emailError = computed(() => {
  if (!email.value) return ''
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailPattern.test(email.value) ? '' : 'Invalid email format'
})

const passwordError = computed(() => {
  if (!password.value) return ''
  if (password.value.length < 8) {
    return 'Password must be at least 8 characters'
  }
  // Check for at least one number and one letter
  const hasNumber = /\d/.test(password.value)
  const hasLetter = /[a-zA-Z]/.test(password.value)
  if (!hasNumber || !hasLetter) {
    return 'Password must contain both letters and numbers'
  }
  return ''
})

const confirmPasswordError = computed(() => {
  if (!confirmPassword.value) return ''
  return confirmPassword.value === password.value ? '' : 'Passwords do not match'
})

const isFormValid = computed(() => {
  return (
    username.value &&
    email.value &&
    password.value &&
    confirmPassword.value &&
    !usernameError.value &&
    !emailError.value &&
    !passwordError.value &&
    !confirmPasswordError.value
  )
})

/**
 * Handle form submission
 * Validates inputs and calls registration API
 */
async function handleSubmit() {
  if (!isFormValid.value) {
    errorMessage.value = 'Please fill in all fields correctly'
    return
  }

  errorMessage.value = ''
  loading.value = true

  try {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: username.value,
        email: email.value,
        password: password.value,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      // Handle error response
      let errorMsg = 'Registration failed'
      if (typeof data.detail === 'string') {
        errorMsg = data.detail
      } else if (Array.isArray(data.detail)) {
        errorMsg = data.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ')
      } else if (data.detail && typeof data.detail === 'object') {
        errorMsg = data.detail.msg || data.detail.message || JSON.stringify(data.detail)
      } else if (data.message) {
        errorMsg = data.message
      }
      throw new Error(errorMsg)
    }

    // Store tokens in localStorage
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)

    // Emit success event with user data
    emit('success', data.user)
  } catch (err: any) {
    errorMessage.value = err.message || 'Registration failed. Please try again.'
  } finally {
    loading.value = false
  }
}

/**
 * Handle switch to login form
 */
function handleSwitchToLogin() {
  emit('switchToLogin')
}
</script>

<template>
  <div class="register-container">
    <form id="register-form" @submit.prevent="handleSubmit">
      <div class="form-group">
        <label for="register-username">Username</label>
        <input
          id="register-username"
          v-model="username"
          type="text"
          required
          minlength="3"
          :disabled="loading"
          autocomplete="username"
          placeholder="Choose a username"
        />
        <span v-if="usernameError" class="error-text">{{ usernameError }}</span>
      </div>

      <div class="form-group">
        <label for="register-email">Email</label>
        <input
          id="register-email"
          v-model="email"
          type="email"
          required
          :disabled="loading"
          autocomplete="email"
          placeholder="you@example.com"
        />
        <span v-if="emailError" class="error-text">{{ emailError }}</span>
      </div>

      <div class="form-group">
        <label for="register-password">Password</label>
        <div class="password-input-wrapper">
          <input
            id="register-password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            required
            minlength="8"
            :disabled="loading"
            autocomplete="new-password"
            placeholder="At least 8 characters"
          />
          <button
            type="button"
            class="password-toggle"
            @click="showPassword = !showPassword"
            tabindex="-1"
            :aria-label="showPassword ? 'Hide password' : 'Show password'"
          >
            <span class="material-symbols-outlined">
              {{ showPassword ? 'visibility_off' : 'visibility' }}
            </span>
          </button>
        </div>
        <span v-if="passwordError" class="error-text">{{ passwordError }}</span>
      </div>

      <div class="form-group">
        <label for="register-confirm-password">Confirm Password</label>
        <div class="password-input-wrapper">
          <input
            id="register-confirm-password"
            v-model="confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            required
            minlength="8"
            :disabled="loading"
            autocomplete="new-password"
            placeholder="Re-enter your password"
          />
          <button
            type="button"
            class="password-toggle"
            @click="showConfirmPassword = !showConfirmPassword"
            tabindex="-1"
            :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
          >
            <span class="material-symbols-outlined">
              {{ showConfirmPassword ? 'visibility_off' : 'visibility' }}
            </span>
          </button>
        </div>
        <span v-if="confirmPasswordError" class="error-text">{{ confirmPasswordError }}</span>
      </div>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <button type="submit" class="btn-primary register-btn" :disabled="!isFormValid || loading">
        <span v-if="loading">Creating Account...</span>
        <span v-else>Create Account</span>
      </button>
    </form>

    <div class="login-prompt">
      <span>Already have an account?</span>
      <button
        type="button"
        class="login-link"
        :disabled="loading"
        @click="handleSwitchToLogin"
      >
        Sign in
      </button>
    </div>
  </div>
</template>

<style scoped>
.register-container {
  display: flex;
  flex-direction: column;
}

#register-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

#register-form .form-group {
  margin-bottom: 0;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper input {
  flex: 1;
  padding-right: 44px;
}

.password-toggle {
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.5);
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.password-toggle:hover {
  color: rgba(255, 255, 255, 0.8);
}

.password-toggle .material-symbols-outlined {
  font-size: 1.25rem;
}

.error-text {
  display: block;
  color: #ef4444;
  font-size: 0.8125rem;
  margin-top: 0.25rem;
}

.error-message {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #fca5a5;
  padding: 0.625rem 0.875rem;
  border-radius: 8px;
  font-size: 0.8125rem;
}

.register-btn {
  margin-top: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  letter-spacing: 0.025em;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.login-prompt span {
  color: var(--text-muted, rgba(255, 255, 255, 0.5));
  font-size: 0.8125rem;
}

.login-link {
  background: transparent;
  border: none;
  color: var(--accent-primary, #d4a72c);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.login-link:hover {
  color: var(--accent-hover, #e5b933);
  background: rgba(212, 167, 44, 0.1);
}

.login-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
